import { useState } from 'react'
import { api, ROUTES, FEATURES, imgUrl, toFormData } from './api'
import { useAdmin, useAsync, Modal, Field, ImageInput, ErrorState, Loading, Empty, PageHead } from './ui'

export default function Categories() {
  const { toast, confirm } = useAdmin()
  const { data, loading, error, reload } = useAsync(() => Promise.all([api.get(ROUTES.categories), api.get(ROUTES.products)]))
  const [editing, setEditing] = useState(null)
  const [categories, products] = data || [[], []]
  const countOf = (id) => products.filter((p) => p.category_id === id).length

  const remove = async (c) => {
    const n = countOf(c.id)
    const ok = await confirm({
      title: 'Delete category',
      body: n ? `"${c.name}" has ${n} product${n > 1 ? 's' : ''}. Move or delete them first, otherwise the products may be left without a category.` : `Delete "${c.name}"? This cannot be undone.`,
    })
    if (!ok) return
    try {
      await api.del(`${ROUTES.categories}/${c.id}`)
      toast('Category deleted')
      reload()
    } catch (e) {
      toast(e.message, 'err')
    }
  }

  if (error) return <ErrorState error={error} onRetry={reload} />

  return (
    <>
      <PageHead title="Categories" sub={`${categories.length} categories`} actions={<button className="btn primary" onClick={() => setEditing({})}>Add category</button>} />
      {loading ? <Loading /> : categories.length === 0 ? (
        <Empty title="No categories yet" body="Create a category before adding products." />
      ) : (
        <div className="cat-grid">
          {categories.map((c) => (
            <article key={c.id} className="cat">
              <div className="cat-img">{c.image ? <img src={imgUrl(c.image)} alt="" /> : <span className="muted">No image</span>}</div>
              <div className="cat-body">
                <div>
                  <strong>{c.name}</strong>
                  <div className="muted">{countOf(c.id)} products</div>
                </div>
                <div className="actions">
                  <button className="btn sm" onClick={() => setEditing(c)}>Edit</button>
                  <button className="btn sm danger-ghost" onClick={() => remove(c)}>Delete</button>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}
      {editing && <CategoryForm category={editing.id ? editing : null} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); reload() }} />}
    </>
  )
}

function CategoryForm({ category, onClose, onSaved }) {
  const { toast } = useAdmin()
  const [name, setName] = useState(category?.name ?? '')
  const [file, setFile] = useState(null)
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)
  const canPickImage = !category || FEATURES.categoryImageEdit

  const submit = async (e) => {
    e.preventDefault()
    if (!name.trim()) return setError('Enter a category name')
    setSaving(true)
    try {
      if (!category) await api.upload('POST', `${ROUTES.categories}/addcategory`, toFormData({ name: name.trim(), image: file }))
      else if (FEATURES.categoryImageEdit) await api.upload('PUT', `${ROUTES.categories}/${category.id}`, toFormData({ name: name.trim(), image: file }))
      else await api.put(`${ROUTES.categories}/${category.id}`, { name: name.trim() })
      toast(category ? 'Category saved' : 'Category added')
      onSaved()
    } catch (err) {
      toast(err.message, 'err')
      setSaving(false)
    }
  }

  return (
    <Modal
      small
      title={category ? 'Edit category' : 'Add category'}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn primary" form="cat-form" disabled={saving}>{saving ? 'Saving…' : category ? 'Save changes' : 'Add category'}</button>
        </>
      }
    >
      <form id="cat-form" onSubmit={submit} noValidate>
        <Field label="Name" error={error}><input className="input" value={name} onChange={(e) => { setName(e.target.value); setError('') }} autoFocus /></Field>
        <span className="field-label">Image</span>
        {canPickImage ? <ImageInput current={category?.image} file={file} onChange={setFile} /> : (
          <>
            <ImageInput current={category?.image} file={null} onChange={() => {}} />
            <p className="hint">The category image can only be set when the category is created.</p>
          </>
        )}
      </form>
    </Modal>
  )
}
